import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import LauncherEntryPoint from '@databox/ember-integrations/lib/components/Ai/Launcher/LauncherEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

interface GenieLauncherSignature {
	Element: HTMLDivElement;
}

// Floating "Ask Genie" button that toggles the side panel. Mounted from the application template, so its React
// root lives for the whole session and route transitions never remount it; Ember state reaches it only through
// didUpdate.
export default class GenieLauncherComponent extends Component<GenieLauncherSignature> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;

	get isOpen(): boolean {
		return this.aiChatService.isOpen;
	}

	get isDarkThemeActive(): boolean {
		return this.navigationService.theme.isDark;
	}

	@action
	toggleChat(): void {
		if (this.isOpen) {
			this.aiChatService.onUserClose();
		} else {
			this.aiChatService.openChat();
		}
	}

	@action
	setupView(node: HTMLElement): void {
		this.reactElement = this.reactService.create(node as HTMLDivElement);
		this.renderLauncher();
	}

	@action
	teardownView(): void {
		this.reactService.unmount(this.reactElement);
	}

	@action
	renderLauncher(): void {
		if (!this.reactElement) {
			return;
		}

		this.reactService.render(
			this.reactElement,
			LauncherEntryPoint({
				isDarkThemeActive: this.isDarkThemeActive,
				isOpen: this.isOpen,
				onClick: this.toggleChat,
			}),
			getShadowDomMode(this.accountService.email),
		);
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{didUpdate this.renderLauncher this.isOpen this.isDarkThemeActive}}
			{{willDestroy this.teardownView}}
			class={{this.aiChatService.genieLayer}}
			...attributes
		/>
	</template>
}
